import React, { useEffect } from 'react';
import { useEventTracking } from '../hooks/useEventTracking';
import {ContactInfo} from "@/components/ContactInfo.tsx";
import { CONTACT_DETAILS } from '../config/contactDetails';
import { Helmet } from 'react-helmet-async';

export const CookiePolicyPage: React.FC = () => {
    const { trackPageView } = useEventTracking();

    useEffect(() => {
        window.scrollTo(0, 0);
        trackPageView('/cookies');
    }, [trackPageView]);

    return (
        <>
            <Helmet>
                <title>{`Cookie Policy | ${CONTACT_DETAILS.company.name}`}</title>
                <meta name="description" content={`Cookie Policy for ${CONTACT_DETAILS.company.name}. Learn about the cookies we use and how to manage your cookie preferences.`} />
                <meta name="robots" content="noindex, follow" />
                <link rel="canonical" href="https://4castlabs.com/cookies" />
                <meta property="og:title" content={`Cookie Policy | ${CONTACT_DETAILS.company.name}`} />
                <meta property="og:description" content={`Cookie Policy for ${CONTACT_DETAILS.company.name}. Learn about cookies and tracking technologies.`} />
                <meta property="og:url" content="https://4castlabs.com/cookies" />
                <meta property="og:type" content="website" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 py-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white shadow-sm rounded-lg p-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-8">Cookie Policy</h1>

                    <div className="prose prose-lg max-w-none">
                        <p className="text-gray-600 mb-6">
                            <strong>Effective Date:</strong> January 1, 2025
                        </p>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. What Are Cookies?</h2>
                            <p className="text-gray-700 mb-4">
                                Cookies are small text files that are stored on your device (computer, tablet, or mobile phone)
                                when you visit our website. They help us provide you with a better experience by remembering your
                                preferences, understanding how you use our services, and improving website functionality.
                            </p>
                            <p className="text-gray-700 mb-4">
                                This Cookie Policy explains how {CONTACT_DETAILS.company.name} uses cookies and similar tracking technologies
                                on our website, in compliance with Canadian privacy laws including the Personal Information Protection Act (PIPA).
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Types of Cookies We Use</h2>

                            <h3 className="text-xl font-medium text-gray-900 mb-3">2.1 Essential Cookies</h3>
                            <p className="text-gray-700 mb-4">
                                These cookies are strictly necessary for our website to function properly and cannot be disabled.
                                They enable core functionality and security features:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                                <li>Session management for assessments and user interactions</li>
                                <li>Security features and authentication</li>
                                <li>Load balancing and website stability</li>
                                <li>Cookie consent preferences</li>
                                <li>Form submission and data processing</li>
                            </ul>
                            <p className="text-gray-700 mb-4">
                                <strong>Legal basis:</strong> These cookies are necessary for the performance of our services and
                                legitimate business interests.
                            </p>

                            <h3 className="text-xl font-medium text-gray-900 mb-3">2.2 Analytics and Performance Cookies</h3>
                            <p className="text-gray-700 mb-4">
                                We use analytics cookies to understand how visitors interact with our website, which helps us
                                improve our services and user experience:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                                <li>Page views, user behavior tracking, and navigation patterns</li>
                                <li>Assessment completion rates and user engagement metrics</li>
                                <li>Website performance monitoring and error tracking</li>
                                <li>Traffic sources and referral analysis</li>
                                <li>Device and browser usage statistics (anonymized)</li>
                            </ul>
                            <p className="text-gray-700 mb-4">
                                <strong>Legal basis:</strong> Your consent, which you can withdraw at any time.
                            </p>

                            <h3 className="text-xl font-medium text-gray-900 mb-3">2.3 Functional Cookies</h3>
                            <p className="text-gray-700 mb-4">
                                These cookies enable enhanced functionality and personalization features:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                                <li>Remembering your preferences and settings</li>
                                <li>Saving assessment progress between sessions</li>
                                <li>Language and region preferences</li>
                                <li>Customized content and recommendations</li>
                            </ul>
                            <p className="text-gray-700 mb-4">
                                <strong>Legal basis:</strong> Your consent and legitimate interests in providing enhanced services.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Third-Party Cookies and Services</h2>
                            <p className="text-gray-700 mb-4">
                                We may use third-party services that set their own cookies on our website. These services help us
                                deliver and improve our services:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li><strong>Content Delivery Networks (CDNs):</strong> For faster loading of website resources and improved performance</li>
                                <li><strong>Analytics Services:</strong> To understand website usage patterns and user behavior (data is anonymized where possible)</li>
                                <li><strong>Email Services:</strong> For contact form submissions and assessment result delivery</li>
                                <li><strong>Security Services:</strong> For protection against malicious attacks and spam</li>
                            </ul>
                            <p className="text-gray-700 mb-4">
                                We ensure that third-party services we use have appropriate privacy protections and data processing agreements in place.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Managing Your Cookie Preferences</h2>

                            <h3 className="text-xl font-medium text-gray-900 mb-3">4.1 Cookie Consent Banner</h3>
                            <p className="text-gray-700 mb-4">
                                When you first visit our website, you'll see a cookie consent banner that allows you to:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                                <li>Accept all cookies</li>
                                <li>Reject non-essential cookies</li>
                                <li>Access detailed information about our cookie practices</li>
                            </ul>

                            <h3 className="text-xl font-medium text-gray-900 mb-3">4.2 Browser Settings</h3>
                            <p className="text-gray-700 mb-4">
                                You can control cookies through your browser settings. Here's how to manage cookies in popular browsers:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                                <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
                                <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
                                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                                <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
                            </ul>

                            <h3 className="text-xl font-medium text-gray-900 mb-3">4.3 Withdrawing Consent</h3>
                            <p className="text-gray-700 mb-4">
                                You can withdraw your consent for non-essential cookies at any time by:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                                <li>Clearing your browser cookies and revisiting our site</li>
                                <li>Contacting us directly using the information in Section 7</li>
                                <li>Using browser settings to block cookies from our domain</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Impact of Disabling Cookies</h2>
                            <p className="text-gray-700 mb-4">
                                If you choose to disable certain types of cookies, some features of our website may not function properly:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li><strong>Essential cookies:</strong> Disabling these may prevent basic website functionality, form submissions, and secure access</li>
                                <li><strong>Analytics cookies:</strong> Disabling these won't affect functionality but prevents us from improving our services based on usage data</li>
                                <li><strong>Functional cookies:</strong> Disabling these may result in loss of personalized features and saved preferences</li>
                            </ul>
                            <p className="text-gray-700 mb-4">
                                We respect your choice to disable cookies while ensuring essential website functions remain available.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention and Security</h2>
                            <p className="text-gray-700 mb-4">
                                Information collected through cookies is retained for different periods based on the type of cookie:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li><strong>Session cookies:</strong> Deleted when you close your browser</li>
                                <li><strong>Persistent cookies:</strong> Stored for specific periods (typically 30 days to 2 years)</li>
                                <li><strong>Analytics data:</strong> Anonymized and retained for up to 2 years for trend analysis</li>
                            </ul>
                            <p className="text-gray-700 mb-4">
                                We implement appropriate security measures to protect cookie data and ensure it is not accessible to unauthorized parties.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Updates to This Cookie Policy</h2>
                            <p className="text-gray-700 mb-4">
                                We may update this Cookie Policy from time to time to reflect changes in our practices,
                                technology, or legal requirements. Any changes will be posted on this page with an updated
                                effective date. We encourage you to review this policy periodically.
                            </p>
                            <p className="text-gray-700 mb-4">
                                For significant changes that affect your rights, we will provide additional notice as required by applicable law.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Contact Us</h2>
                            <p className="text-gray-700 mb-4">
                                If you have questions about our use of cookies or this Cookie Policy, please contact us:
                            </p>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <ContactInfo
                                    variant="default"
                                    className="space-y-3"
                                    showPhone={false}
                                    emailType='privacy'
                                />
                                <p className="text-sm text-gray-600 mt-3">
                                    Please mark correspondence "Attention: Privacy Officer" for privacy-related inquiries.
                                </p>
                            </div>
                            <p className="text-gray-700 mt-4">
                                For more information about your privacy rights under Canadian law, you may also contact the
                                Information and Privacy Commissioners in
                                <a href="https://www.oipc.bc.ca" className="text-primary-600 hover:text-primary-700 underline ml-1">British Columbia</a> or
                                <a href="https://www.oipc.ab.ca" className="text-primary-600 hover:text-primary-700 underline ml-1">Alberta</a>.
                            </p>
                        </section>
                    </div>
                </div>
            </div>
            </div>
        </>
    );
};